namespace InvData.DecimalHelper {

    let _thousandsSeparator = ",";
    let _decimalSeparator = ".";


    function isNull(obj) {
        return typeof obj == "undefined" || obj === null;
    }

    export function isNegative(number: number | string): boolean {
        if (isNull(number)) {
            return false;
        }

        if (typeof (number) === "number") {
            return number < 0;
        }
        else if (typeof (number) === "string") {
            let numberStr = number.toString().trim();
            if (numberStr.length > 0 && numberStr[0] === '-') {
                return true;
            }
        }

        return false;
    }

    export function isNullOrZero(number: number | string) {
        if (isNull(number)) {
            return true;
        }

        if (typeof (number) === "number") {
            return (number == 0);
        }
        else if (typeof (number) === "string") {
            let numberParsed = InvData.DecimalHelper.decimalStringAsNumber(number);
            return (numberParsed == 0);
        }

        return false;
    }

    export function negate(number: number | string): number | string {
        if (isNull(number)) {
            return null;
        }

        if (typeof (number) === "number") {
            return -number;
        }
        else if (typeof (number) === "string") {
            let numberStr = number.toString().trim();
            if (numberStr.length > 0 && numberStr[0] === '-') {
                let negatedNumberStr = numberStr.slice(1);
                return negatedNumberStr;
            }
            else {
                let negatedNumberStr = "-" + numberStr;
                return negatedNumberStr;
            }
        }

        return null;
    }


    /**
     * Retrieve number of digits after the decimal.
     * @param number
     */
    export function getScale(number): number {
        if (isNull(number)) {
            return 0;
        }

        let numberStr = number.toString().trim();

        let decimalFound: boolean = false;
        let scale = 0;

        let index = 0;
        while (index < numberStr.length) {
            let ch = numberStr[index];
            if (decimalFound) {
                scale++;
            }
            else {
                if (ch === _decimalSeparator) {
                    // NOTE: This will need to be adapted for internationalization when commas and periods get switched up. Same for rest of this class.
                    decimalFound = true;
                }
            }
            index++;
        }

        return scale;
    }

    export function decimalStringAsNumber(number, treatNullAsZero: boolean = true): number {
        if (isNull(number)) {
            return treatNullAsZero ? 0 : null;
        }

        if (typeof (number) === "number") {
            return number;
        }
        else if (typeof (number) === "string") {
            let numberStr = number.trim();
            if (numberStr.length == 0) {
                return decimalStringAsNumber(null, treatNullAsZero);
            }

            let numberOnlyStr = "";
            let index = 0;
            while (index < numberStr.length) {
                let ch = numberStr[index];
                if (ch === '-' || ch === _decimalSeparator || (ch >= '0' && ch <= '9')) {
                    numberOnlyStr += ch;
                }
                index++;
            }

            let actualNumber = parseFloat(numberOnlyStr);
            return actualNumber;
        }
        else {
            throw "Unexpected type " + typeof (number);
        }
    }

    /**
     * Take a decimal number and clamp it to a particular scale, eg. "2.12345" at scale of 2 is "2.12".
     */
    export function clampNumberToScaleAsString(number: string | number, scale: number): string {
        if (isNull(number)) {
            return null;
        }

        if (scale < 0) {
            scale = 0;
        }

        var numberStr = number.toString().trim();
        if (numberStr.length == 0) {
            return null;
        }

        var isNegative = false;
        if (numberStr[0] == '-') {
            isNegative = true;
        }

        var decimalIndex = numberStr.indexOf(".");
        if (decimalIndex >= 0) {
            var currentScale = numberStr.length - decimalIndex - 1;
            if (currentScale > scale) {
                var originalNumberStr = numberStr;
                var newLength = numberStr.length - (currentScale - scale);

                var needToRound = false;
                var firstDigitRemoved = originalNumberStr[newLength];
                if (firstDigitRemoved >= "0" && firstDigitRemoved <= "9") {
                    if (firstDigitRemoved >= "5") {
                        needToRound = true;
                    }
                }

                if (numberStr[newLength - 1] == _decimalSeparator) {
                    // Get rid of trailing decimal.
                    newLength--;
                }

                numberStr = numberStr.substr(0, newLength);

                if (needToRound) {
                    var addOneToTheStart = false;
                    var charArray = numberStr.split('');
                    for (var co = charArray.length - 1; co >= 0; co--) {
                        var ch = charArray[co];
                        if (ch >= "0" && ch <= "9") {
                            if (ch == "9") {
                                // We keep going because the next digit needs to be rounded.
                                charArray[co] = "0";
                                addOneToTheStart = true;
                            }
                            else {
                                // We stop because there is no reason to continue carrying the rounding back.
                                charArray[co] = String.fromCharCode(ch.charCodeAt(0) + 1);
                                addOneToTheStart = false;
                                break;
                            }
                        }
                    }

                    if (addOneToTheStart) {
                        if (charArray[0] == "-") {
                            charArray.splice(1, 0, "1");
                        }
                        else {
                            charArray.splice(0, 0, "1");
                        }
                    }

                    numberStr = charArray.join("");
                }
            }
        }

        if (numberStr === "-0") {
            numberStr = "0";
        }

        return numberStr;
    }


    /**
     * Take number or string-based number and return string that has the thousands separator everywhere. If the number is in scientific notation,
     * null will be returned.
     * @param number
     */
    export function fixupThousandsSeparatorAsNumberString(number: number | string): string {
        if (typeof (number) === "undefined" || isNull(number)) {
            return null;
        }
        
        let numberStr: string;
        if (typeof (number) === "number") {
            numberStr = number.toString();
        }
        else if (typeof (number) === "string") {
            numberStr = number;
        }
        else {
            return null;
        }

        if (numberStr.length === 0) {
            return null;
        }

        if (numberStr.indexOf("e-") !== -1 || numberStr.indexOf("e+") !== -1) {
            return null;
        }

        let isNegative = InvData.DecimalHelper.isNegative(numberStr);

        let numberAfterDecimal: string = "";
        let lastNonZeroIndexAfterDecimal: number = -1;

        let digitsBeforeDecimal: string[] = [];

        let decimalFound: boolean = false;
 
        let index = 0;
        while (index < numberStr.length) {
            let ch = numberStr[index];
            if (decimalFound) {
                if (ch >= '0' && ch <= '9') {
                    numberAfterDecimal += ch;
                    if (ch != "0") {
                        lastNonZeroIndexAfterDecimal = numberAfterDecimal.length - 1;
                    }
                }
            }
            else {
                if (ch === _decimalSeparator) {
                    // NOTE: This will need to be adapted for internationalization when commas and periods get switched up. Same for rest of this class.
                    decimalFound = true;
                }
                else if (ch >= '0' && ch <= '9') {
                    digitsBeforeDecimal.push(ch);
                }
            }
            index++;
        }

        let finalNumberStr = isNegative ? "-" : "";

        if (digitsBeforeDecimal.length > 0) {
            let numberBeforeDecimal = "";

            let leftInBlock = 3;
            while (digitsBeforeDecimal.length > 0) {
                if (leftInBlock === 0) {
                    numberBeforeDecimal = _thousandsSeparator + numberBeforeDecimal;
                    leftInBlock = 3;
                }

                let digit = digitsBeforeDecimal.pop();
                numberBeforeDecimal = digit + numberBeforeDecimal;
                leftInBlock--;
            }

            if (numberBeforeDecimal.length > 0) {
                finalNumberStr += numberBeforeDecimal;
            }
        }        

        if (numberAfterDecimal.length > 0 && lastNonZeroIndexAfterDecimal > -1) {
            finalNumberStr += _decimalSeparator;
            finalNumberStr += numberAfterDecimal.substr(0, lastNonZeroIndexAfterDecimal + 1);
        }

        return finalNumberStr;
    }
    

    /**
     * Take string-based number and return string that has zeros
     * @param numberString
     */
    export function setTrailingZeros(numberString: string): string {
         if (numberString == null) {
                return null;
            }

            if (numberString.indexOf('.') != -1) {
                var numberStringParts = numberString.split('.');
                if (numberStringParts[1].length <= 2) {
                    return numberStringParts[0] + '.' + Number(numberString.split(',').join('')).toFixed(2).toString().split('.')[1];
                }
                else {
                    return numberString;
                }
            }
            else {
                return numberString + '.00';
            }
    }

    /**
     * Take string-based number and return string that has zeros
     * @param numberString
     */
    export function cleanNumberInputString(numberString: string): string {
        if (numberString === null) {
            return null;
        }
        return numberString.toString().replace(/[^0-9.+-]/g, "");
    }
}